// @ts-strict-ignore
import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import Spin from 'antd/lib/spin';
import { ErrorFallback } from './ErrorBoundary';
import { flatMap } from 'lodash';
import * as React from 'react';
import {
    IntegrationFirebaseBeta,
    IntegrationInstallationId,
    IntegrationLaunchDarkly,
    IntegrationOptimizely,
    IntegrationProviderType,
    IntegrationType,
} from '../types';
import { integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { RootState } from '../reducers/reducers';
import {
    selectAppSettings,
    selectAppStoreType,
    selectOrgProviders,
    selectAppName,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import { useParams } from 'react-router-dom';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { oauthInstallationUrlForIntegrationType, validateUpdateIntegrationValues } from '../helpers/integrationHelpers';
import Link from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';
import { updateIntegrationFormDataForIntegration } from '../helpers/integrationFormHelpers';
import { UpdateIntegrationModalData } from './setup/useUpdateIntegrationModal';
import { globalMessage } from '../utils/globalMessage';
import { useEffect } from 'react';
import { useIntegrationSettingFields } from '../utils/hooks/useIntegrationSettingFields';

interface IntegrationConfigurationProps extends UpdateIntegrationModalData {
    onClose: () => void;
}

const IntegrationConfiguration: React.FC<IntegrationConfigurationProps> = (props: IntegrationConfigurationProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);
    const { settingFields, isLoadingSettingFields, isSettingFieldsError } = useIntegrationSettingFields(
        props.orgId,
        props.appId,
        props.integration.installationId,
    );

    const [selectedOptimizelyProjectId, setSelectedOptimizelyProjectId] = React.useState(
        (props.integration as IntegrationOptimizely)?.data.selectedProject?.id ?? null,
    );

    const [selectedLaunchDarklyProjectId, setSelectedLaunchDarklyProjectId] = React.useState(
        (props.integration as IntegrationLaunchDarkly)?.data.selectedProject?.id ?? null,
    );

    const [selectedFirebaseProjectId, setSelectedFirebaseProjectId] = React.useState(
        (props.integration as IntegrationFirebaseBeta)?.data.projectId ?? null,
    );

    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration?.providerId);

        return provider ?? null;
    });

    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, props.appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;
        setFormIsValid(isValid);
    };

    const onSubmit = (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                // BE-defined fields already use the save payload's shape
                const requestData =
                    settingFields ? values : (
                        validateUpdateIntegrationValues(integrationType, integrationProvider, values)
                    );

                if (requestData) {
                    props.updateIntegration(integrationInstallationId, integrationType, requestData);
                } else {
                    throw Error('Invalid form values');
                }
            })
            .catch((e) => {
                globalMessage.error(e.message);
                setFormIsValid(false);
            });
    };

    const onClose = () => {
        form.resetFields();

        props.onClose();
    };

    const onChangeOptimizelyProjectId = (value) => {
        form.resetFields(['environmentId']);
        setSelectedOptimizelyProjectId(value);
    };

    const onChangeLaunchDarklyProjectId = (value) => {
        form.resetFields(['environmentId']);
        setSelectedLaunchDarklyProjectId(value);
    };

    const onChangeFirebaseProjectId = (value) => {
        form.resetFields(['firebaseAppId', 'selectedBetaGroupIds']);
        setSelectedFirebaseProjectId(value);
    };

    const [form] = useForm();

    const selectedAutoSelectAllJiraProjects = useWatch(['autoSelectAllProjects'], form);
    useEffect(() => {
        form.resetFields(['projects']);
    }, [selectedAutoSelectAllJiraProjects]);

    const fields = updateIntegrationFormDataForIntegration(
        props.integration,
        settingFields,
        appPlatform,
        appStoreType,
        props.appId,
        appName,
        workflowEnabledSteps,
        integrationProvider,
        onChangeOptimizelyProjectId,
        selectedOptimizelyProjectId,
        onChangeLaunchDarklyProjectId,
        selectedLaunchDarklyProjectId,
        onChangeFirebaseProjectId,
        selectedFirebaseProjectId,
        selectedAutoSelectAllJiraProjects,
    );
    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onSubmit,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
            )
        :   null;

    return (
        <Modal
            title={`Complete ${props.integration.name} setup`}
            open={props.open}
            confirmLoading={props.isLoading}
            maskClosable={props.closable}
            closable={props.closable}
            onCancel={onClose}
            footer={
                isSettingFieldsError ? null : (
                    <Button
                        onClick={() => form.submit()}
                        disabled={!formIsValid}
                        loading={props.isLoading}
                        type="primary"
                    >
                        Submit
                    </Button>
                )
            }
            centered
            destroyOnClose
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
        >
            {isSettingFieldsError ?
                <ErrorFallback />
            :   <div>
                    {isLoadingSettingFields ?
                        <Spin />
                    :   modalContent}
                    {additionalInfoForProvider && <div style={{ marginTop: 12 }}>{additionalInfoForProvider}</div>}
                </div>
            }
        </Modal>
    );
};

export default IntegrationConfiguration;

export const additionalInfoNodeForProvider = (
    providerType: IntegrationProviderType,
    integrationType: IntegrationType,
    orgId: string,
    orgIdParam: string,
    appIdParam: string,
    integrationInstallationId?: IntegrationInstallationId,
) => {
    if (providerType === IntegrationProviderType.GitHub) {
        const installURL = oauthInstallationUrlForIntegrationType(
            integrationType,
            orgId,
            orgIdParam,
            appIdParam,
            undefined,
            integrationInstallationId,
        );
        return (
            <Text>
                Don&apos;t see your repo? <Link href={installURL}>Authorize more repos.</Link>
            </Text>
        );
    }

    return null;
};
